import { createContext, useEffect } from 'react';
import { useLazyQuery, useMutation, useQuery } from 'data-fetching-client';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import ProductUpdatesCategoryView from './ProductUpdatesCategoryView';
import RolloutProductUpdateDetailsPanel from './DetailsPanel/RolloutProductUpdateDetailsPanel';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { Header } from 'foundations-components/transitional/components/Header';
import { ListingPage } from 'foundations-components/transitional/components/ListingPage';
import { categories } from '../CategoryTypes';
import { NavMarker } from 'react-rhumb';
import { useGetQueryParam } from '../../app/hooks/useGetQueryParam';
import { useSetQueryParams } from '../../app/hooks/useSetQueryParams';
import { TrackingScreen, TrackingSubscreen, useTrackPageview } from '../../app/utils/tracking';
import ManageReleaseSettingsLink from './ManageReleaseSettingsLink';
import { useRawParamsForListRolloutProductUpdates } from '../hooks/useRawParamsForListRolloutProductUpdates';
import { ScheduledUpdatesViews } from '../constants';
// @ts-expect-error Library not migrated yet
import { useFeedbackLoader } from 'feedback-loader/useFeedbackLoader';
import Enviro from 'enviro';
import { GATES, PROD_SURVEY_ID, QA_SURVEY_ID } from '../../app/utils/constants';
import { selectAuthenticatedUser } from '../../app/auth/AuthSelectors';
import { useAppSelector } from '../../app/indexReducer';
import useMarkPUViewed from '../hooks/useMarkPUViewed';
import { getRequestParamsForGetRolloutProductUpdates } from '../utils';
import SideNav from './SideNav';
import ErrorBoundary from '../../app/components/ErrorBoundary';
import useFetchOrgData from '../hooks/useFetchOrgData';
import { GET_FEATURE_GROUPS, GET_NEW_TO_YOU_TIMESTAMP, UPDATE_NEW_TO_YOU_TIMESTAMP } from '../queries';
import GateWrapper from './utils/GateWrapper';
import { selectHasNewToYouGate } from '../../app/auth/GateSelectors';
import { logError } from '../../app/utils/handleError';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const NewToYouContext = /*#__PURE__*/createContext({
  newToYouTimestamp: undefined
});
export const AllFeatureGroupsContext = /*#__PURE__*/createContext({
  isLoadingFeatureGroups: false,
  allFeatureGroups: undefined
});
const IndexPage = () => {
  const {
    loadSurvey,
    clientLoaded
  } = useFeedbackLoader();
  useFetchOrgData();
  const rolloutId = useGetQueryParam('rollout') || '';
  const updateId = useGetQueryParam('update') || '';
  const betaStatus = useGetQueryParam('betaStatus') || '';
  const setQueryParams = useSetQueryParams();
  const {
    view
  } = useRawParamsForListRolloutProductUpdates();
  const rawParams = useRawParamsForListRolloutProductUpdates();
  const user = useAppSelector(selectAuthenticatedUser);
  const userId = user && user.user_id;
  const paramsForGetRolloutProductUpdates = getRequestParamsForGetRolloutProductUpdates(rawParams);
  const {
    category,
    limit,
    offset
  } = rawParams;
  const {
    isLoading: isDBLoading,
    getAllPUError,
    startStageDate,
    newToYouProductUpdates,
    newToYouTotalCount,
    unviewedPUIds,
    schedulePUViewed,
    processAllScheduledViewedPUs
  } = useMarkPUViewed({
    userId,
    paramsForGetRolloutProductUpdates,
    pageSize: limit || 10,
    currentOffset: offset || 0
  });
  const {
    data: allFeatureGroupsData,
    loading: isLoadingFeatureGroups
  } = useQuery(GET_FEATURE_GROUPS);
  const hasNewToYouGate = useAppSelector(selectHasNewToYouGate);
  const [getNewToYouTimestamp, {
    data: newToYouTimestampData
  }] = useLazyQuery(GET_NEW_TO_YOU_TIMESTAMP, {
    fetchPolicy: 'network-only',
    // TODO: remove this after testing
    onCompleted: data => {
      // eslint-disable-next-line no-console
      console.debug(`testing purpose: ${category} getNewToYouTimestamp response`, new Date(data.newToYouTimestamp));
    },
    onError: err => {
      logError({
        error: err,
        componentName: 'IndexPage',
        extra: {
          message: 'Failed to get new to you timestamp'
        }
      });
    }
  });
  useEffect(() => {
    if (hasNewToYouGate && category !== 'new-to-you') {
      getNewToYouTimestamp();
    }
  }, [hasNewToYouGate, category, getNewToYouTimestamp]);
  const [updateNewToYouTimestamp, {
    data: previousNewToYouTimestamp
  }] = useMutation(UPDATE_NEW_TO_YOU_TIMESTAMP, {
    // TODO: remove this after testing
    onCompleted: data => {
      // eslint-disable-next-line no-console
      console.debug(`testing purpose: ${category} updateNewToYouTimestamp response`, new Date(data.updateNewToYouTimestamp));
    },
    onError: err => {
      logError({
        error: err,
        componentName: 'IndexPage',
        extra: {
          message: 'Failed to update new to you timestamp'
        }
      });
    }
  });
  useEffect(() => {
    if (category === 'new-to-you') {
      void updateNewToYouTimestamp();
    }
  }, [hasNewToYouGate, category, updateNewToYouTimestamp]);
  const newToYouTimestamp = category === 'new-to-you' ? previousNewToYouTimestamp === null || previousNewToYouTimestamp === void 0 ? void 0 : previousNewToYouTimestamp.updateNewToYouTimestamp : newToYouTimestampData === null || newToYouTimestampData === void 0 ? void 0 : newToYouTimestampData.newToYouTimestamp;
  useTrackPageview(TrackingScreen.PRODUCT_UPDATES, TrackingSubscreen.ALL);
  useEffect(() => {
    if (clientLoaded) {
      loadSurvey(Enviro.isQa() ? QA_SURVEY_ID : PROD_SURVEY_ID, 'CSAT');
    }
  }, [clientLoaded, loadSurvey]);
  useEffect(() => {
    if (category === 'scheduled-for-release' && !view) {
      setQueryParams({
        view: ScheduledUpdatesViews.CALENDAR,
        calendarTimestamp: String(I18n.moment().valueOf()),
        limit: '',
        offset: ''
      });
    }
  }, [category, view, setQueryParams]);
  useEffect(() => {
    if (category === 'your-betas' && betaStatus !== 'enrolled') {
      setQueryParams({
        betaStatus: 'enrolled'
      });
    }
  }, [betaStatus, category, setQueryParams]);
  const handleClose = () => {
    setQueryParams({
      update: '',
      rollout: ''
    });
  };
  if (category && !categories.includes(category)) {
    window.location.href = `/product-updates/${PortalIdParser.get()}`;
    return /*#__PURE__*/_jsx(_Fragment, {});
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "INDEX_LOAD"
    }), /*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(ListingPage, {
      "data-test-id": "home-page",
      headerComponent: /*#__PURE__*/_jsx(Header, {
        flush: true,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          "data-test-id": "header-message",
          message: "app.pageTitle"
        }),
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          componentName: "ManageReleaseSettingsLink",
          children: /*#__PURE__*/_jsx(ManageReleaseSettingsLink, {})
        })
      }),
      pageLayout: "full-width",
      sidebarComponent: /*#__PURE__*/_jsx(ErrorBoundary, {
        componentName: "SideNav",
        children: /*#__PURE__*/_jsx(GateWrapper, {
          ungatedToAny: [GATES.NEW_TO_YOU_GATE],
          fallbackComponent: /*#__PURE__*/_jsx(SideNav, {
            category: category,
            processAllScheduledViewedPUs: processAllScheduledViewedPUs,
            unviewedPUIds: unviewedPUIds
          }),
          children: /*#__PURE__*/_jsx(NewToYouContext.Provider, {
            value: {
              newToYouTimestamp
            },
            children: /*#__PURE__*/_jsx(SideNav, {
              category: category,
              processAllScheduledViewedPUs: processAllScheduledViewedPUs,
              unviewedPUIds: unviewedPUIds
            })
          })
        })
      }),
      children: /*#__PURE__*/_jsxs(AllFeatureGroupsContext.Provider, {
        value: {
          allFeatureGroups: allFeatureGroupsData === null || allFeatureGroupsData === void 0 ? void 0 : allFeatureGroupsData.featureGroups,
          isLoadingFeatureGroups
        },
        children: [/*#__PURE__*/_jsx(GateWrapper, {
          ungatedToAny: [GATES.NEW_TO_YOU_GATE],
          fallbackComponent: /*#__PURE__*/_jsx(ProductUpdatesCategoryView, {
            isDBLoading: isDBLoading,
            getAllPUError: getAllPUError,
            startStageDate: startStageDate,
            newToYouProductUpdates: newToYouProductUpdates,
            newToYouTotalCount: newToYouTotalCount,
            unviewedPUIds: unviewedPUIds,
            schedulePUViewed: schedulePUViewed,
            processAllScheduledViewedPUs: processAllScheduledViewedPUs
          }),
          children: /*#__PURE__*/_jsx(NewToYouContext.Provider, {
            value: {
              newToYouTimestamp
            },
            children: /*#__PURE__*/_jsx(ProductUpdatesCategoryView, {
              isDBLoading: isDBLoading,
              getAllPUError: getAllPUError,
              startStageDate: startStageDate,
              newToYouProductUpdates: newToYouProductUpdates,
              newToYouTotalCount: newToYouTotalCount,
              unviewedPUIds: unviewedPUIds,
              schedulePUViewed: schedulePUViewed,
              processAllScheduledViewedPUs: processAllScheduledViewedPUs
            })
          })
        }), (rolloutId || updateId) && /*#__PURE__*/_jsx(ErrorBoundary, {
          componentName: "RolloutProductUpdateDetailsPanel",
          children: /*#__PURE__*/_jsx(RolloutProductUpdateDetailsPanel, {
            handleClose: handleClose
          })
        })]
      })
    })]
  });
};
export default IndexPage;